/**
 * «API» (`/integrations`): все каналы, к которым подключён prokat, — статус, последние входящие и
 * исходящие, каждая проверка с тем, что она увидела. «Проверить сейчас» — тот же проход, что у фона
 * `control_scan` (раз в 5 минут); «Тест ботами» — три бота пишут владельцу. Вкладка «Контроль»
 * (`?tab=alerts`) — журнал сбоев и правила. Открыт всем вошедшим.
 * Спека `docs/specs/2026-10-22-control-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";

import {
  ackAlerts,
  type Alert,
  type Channel,
  type Check,
  type ControlReport,
  loadAlerts,
  loadControl,
  runScan,
  runTestSend,
  type State,
  type TestResult,
} from "@/lib/control";
import { card } from "@/pages/AppointmentsUi";

const pill = (on: boolean) =>
  `rounded-full border px-2.5 py-0.5 text-[12px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;
const btn =
  "rounded-[var(--radius-ui)] border border-border px-2.5 py-1 text-[12px] text-fg-muted hover:text-fg disabled:opacity-50";

const DOT: Record<State, string> = { ok: "bg-ok", warn: "bg-warn", fail: "bg-err" };
const TEXT: Record<State, string> = { ok: "text-fg-muted", warn: "text-warn", fail: "text-err" };
const WORD: Record<State, string> = { ok: "работает", warn: "внимание", fail: "сбой" };

/** `2026-10-22T13:42:08` → «13:42», не сегодня — «21.10 13:42». */
function when(iso: string | null | undefined, today: string): string {
  if (!iso) return "—";
  const t = iso.slice(11, 16);
  return iso.slice(0, 10) === today ? t : `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${t}`;
}

function Dot({ state }: { state: State | null }) {
  return <span className={`inline-block size-2.5 shrink-0 rounded-full ${state ? DOT[state] : "bg-border-strong"}`} />;
}

function CheckRow({ c, today }: { c: Check; today: string }) {
  return (
    <tr className="border-t border-border align-top" data-annot={`проверка ${c.code}`}>
      <td className="py-1.5 pl-4 pr-2">
        <Dot state={c.state} />
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">
        <span className="text-fg">{c.label}</span>
        {c.target && <span className="ml-1.5 font-mono text-[11.5px] text-fg-muted">{c.target}</span>}
        <div className="text-[11px] text-fg-faint">
          {c.live ? "живьём · " : c.test ? "" : "по базе · "}
          {c.how}
        </div>
      </td>
      <td className={`px-2 py-1.5 ${TEXT[c.state]}`}>
        {c.value}
        {c.detail && <div className="max-w-[640px] break-words text-[11.5px] text-fg-faint">{c.detail}</div>}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-right text-[11.5px] text-fg-faint" title={`в этом состоянии с ${when(c.since, today)}`}>
        {when(c.checked_at, today)}
      </td>
    </tr>
  );
}

function Activity({ ch, today }: { ch: Channel; today: string }) {
  const a = ch.activity;
  if (!a.in && !a.out) return null;
  const side = (label: string, s?: { last: string | null; today: number }) =>
    s && (
      <span>
        <span className="text-fg-faint">{label}:</span> {when(s.last, today)} · сегодня {s.today}
      </span>
    );
  return (
    <div className="flex flex-wrap gap-x-5 text-[12px] text-fg-muted">
      {side("входящее", a.in)}
      {side("исходящее", a.out)}
    </div>
  );
}

function ChannelCard({ ch, today, open }: { ch: Channel; today: string; open: boolean }) {
  const [shown, setShown] = useState(open);
  return (
    <section className={card} data-annot={`канал ${ch.label}`}>
      <button type="button" onClick={() => setShown((v) => !v)} className="flex w-full flex-col gap-1 px-4 py-2.5 text-left hover:bg-bg-hover">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Dot state={ch.state} />
          <h2 className="text-[13.5px] font-medium">{ch.label}</h2>
          <span className={`text-[12px] ${ch.state ? TEXT[ch.state] : "text-fg-faint"}`}>{ch.state ? WORD[ch.state] : "не проверяли"}</span>
          {ch.alerts > 0 && <span className="rounded-full bg-err-bg px-2 text-[11.5px] text-err">открытых сбоев {ch.alerts}</span>}
          <span className="ml-auto text-[11.5px] text-fg-faint">{ch.checked_at ? `проверено ${when(ch.checked_at, today)}` : ""}</span>
        </div>
        <p className="text-[11.5px] text-fg-faint">{ch.how}</p>
        <Activity ch={ch} today={today} />
        {ch.problem && <p className={`text-[12.5px] ${ch.state ? TEXT[ch.state] : ""}`}>{ch.problem}</p>}
      </button>
      {shown && ch.checks.length > 0 && (
        <div className="overflow-x-auto border-t border-border">
          <table className="w-full text-[12.5px]">
            <tbody>
              {ch.checks.map((c) => (
                <CheckRow key={`${c.code}|${c.target}`} c={c} today={today} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Channels({ d }: { d: ControlReport }) {
  const today = d.now.slice(0, 10);
  return (
    <>
      {d.channels.map((ch) => (
        <ChannelCard key={ch.code} ch={ch} today={today} open={ch.state === "fail" || ch.state === "warn"} />
      ))}
    </>
  );
}

const CHANNEL_LABEL = (d: ControlReport | undefined, code: string) => d?.channels.find((c) => c.code === code)?.label ?? code;

function Alerts({ d }: { d: ControlReport | undefined }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["control-alerts"], queryFn: () => loadAlerts(30) });
  const ack = useMutation({
    mutationFn: ackAlerts,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["control-alerts"] });
      void qc.invalidateQueries({ queryKey: ["control"] });
    },
  });
  const rows = q.data ?? [];
  const today = (d?.now ?? new Date().toISOString()).slice(0, 10);
  const unseen = rows.filter((a) => !a.seen_at).map((a) => a.id);
  return (
    <>
      <section className={card} data-annot="журнал сбоев">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2.5">
          <h2 className="text-[13.5px] font-medium">Сбои за 30 дней · {rows.length}</h2>
          <span className="text-[12px] text-fg-faint">открыт после нужного числа провалов подряд, закрыт, когда проверка снова «работает»</span>
          {unseen.length > 0 && (
            <button type="button" className={`${btn} ml-auto`} disabled={ack.isPending} onClick={() => ack.mutate(unseen)}>
              ✓ Увидел всё · {unseen.length}
            </button>
          )}
        </div>
        {q.isPending && <p className="px-4 py-3 text-[12.5px] text-fg-muted">Загружаю…</p>}
        {!q.isPending && rows.length === 0 && <p className="px-4 py-3 text-[12.5px] text-fg-muted">Сбоев не было.</p>}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[11.5px] text-fg-faint">
                <tr>
                  <th className="px-4 py-1.5 text-left font-medium">Открылся</th>
                  <th className="px-3 py-1.5 text-left font-medium">Канал</th>
                  <th className="px-3 py-1.5 text-left font-medium">Что</th>
                  <th className="px-3 py-1.5 text-left font-medium">Закрылся</th>
                  <th className="px-3 py-1.5 text-left font-medium">В личку</th>
                  <th className="px-3 py-1.5 text-left font-medium">Увидел</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <AlertRow key={a.id} a={a} d={d} today={today} onAck={() => ack.mutate([a.id])} busy={ack.isPending} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {d && <Rules d={d} />}
    </>
  );
}

function AlertRow({ a, d, today, onAck, busy }: { a: Alert; d: ControlReport | undefined; today: string; onAck: () => void; busy: boolean }) {
  const open = !a.resolved_at;
  return (
    <tr className={`border-t border-border align-top ${open ? "bg-err/5" : ""}`} data-annot={`сбой ${a.id}`}>
      <td className="whitespace-nowrap px-4 py-1.5 text-fg-muted">{when(a.opened_at, today)}</td>
      <td className="whitespace-nowrap px-3 py-1.5">{CHANNEL_LABEL(d, a.channel)}</td>
      <td className="px-3 py-1.5">
        <span className={open ? "text-err" : ""}>{a.title}</span>
        {a.detail && <div className="max-w-[560px] break-words text-[11.5px] text-fg-faint">{a.detail}</div>}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">{open ? <span className="text-err">открыт</span> : when(a.resolved_at, today)}</td>
      <td className="whitespace-nowrap px-3 py-1.5 text-fg-muted">
        {a.sent_at ? when(a.sent_at, today) : "—"}
        {a.resolved_sent_at && <span className="text-fg-faint"> · 🟢 {when(a.resolved_sent_at, today)}</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {a.seen_at ? (
          <span className="text-fg-faint" title={a.seen_by ?? ""}>
            {when(a.seen_at, today)}
          </span>
        ) : (
          <button type="button" className={btn} disabled={busy} onClick={onAck}>
            увидел
          </button>
        )}
      </td>
    </tr>
  );
}

function Rules({ d }: { d: ControlReport }) {
  return (
    <section className={card} data-annot="правила контроля">
      <div className="border-b border-border px-4 py-2.5">
        <h2 className="text-[13.5px] font-medium">Что проверяем · {d.rules.length}</h2>
        <p className="text-[12px] text-fg-faint">
          Фон — раз в 5 минут. «Живьём» — запрос в API площадки, «по базе» — по своим журналам. Сбой открывается, когда проверка красная столько проходов
          подряд, сколько указано; в личку уходит сразу и ещё раз, когда восстановилось.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Канал</th>
              <th className="px-3 py-1.5 text-left font-medium">Проверка</th>
              <th className="px-3 py-1.5 text-left font-medium">Как и порог</th>
              <th className="px-3 py-1.5 text-right font-medium">Подряд</th>
            </tr>
          </thead>
          <tbody>
            {d.rules.map((r) => (
              <tr key={r.code} className="border-t border-border">
                <td className="whitespace-nowrap px-4 py-1.5 text-fg-muted">{CHANNEL_LABEL(d, r.channel)}</td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {r.label} <span className="font-mono text-[11px] text-fg-faint">{r.code}</span>
                </td>
                <td className="px-3 py-1.5 text-fg-muted">
                  <span className="text-fg-faint">{r.live ? "живьём · " : "по базе · "}</span>
                  {r.how}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.repeat}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const TEST_LABEL: Record<string, string> = { staffbot: "@unirenteraibot", tgbot: "@unirenterbot", maxbot: "бот MAX" };

export function IntegrationsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "alerts" ? "alerts" : "channels";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["control"], queryFn: loadControl, refetchInterval: 60_000 });
  const scan = useMutation({
    mutationFn: runScan,
    onSuccess: (r) => {
      qc.setQueryData(["control"], r);
      void qc.invalidateQueries({ queryKey: ["control-alerts"] });
    },
  });
  const [tests, setTests] = useState<TestResult[] | null>(null);
  const test = useMutation({
    mutationFn: runTestSend,
    onSuccess: (r) => {
      setTests(r);
      void qc.invalidateQueries({ queryKey: ["control"] });
    },
  });
  const d = q.data;
  const counts = d ? { fail: d.channels.filter((c) => c.state === "fail").length, warn: d.channels.filter((c) => c.state === "warn").length } : null;
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-col gap-2" data-annot="шапка раздела API">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[16px] font-semibold tracking-[-0.015em]">API</h1>
          <div className="flex gap-1" role="radiogroup" aria-label="Вид">
            <button type="button" role="radio" aria-checked={tab === "channels"} onClick={() => setParams({}, { replace: true })} className={pill(tab === "channels")}>
              Каналы
            </button>
            <button type="button" role="radio" aria-checked={tab === "alerts"} onClick={() => setParams({ tab: "alerts" }, { replace: true })} className={pill(tab === "alerts")}>
              Контроль{d && d.open.length > 0 ? ` · ${d.open.length}` : ""}
            </button>
          </div>
          {counts && (
            <span className="text-[12px] text-fg-muted">
              каналов {d!.channels.length}: <span className={counts.fail ? "text-err" : ""}>сбой {counts.fail}</span> ·{" "}
              <span className={counts.warn ? "text-warn" : ""}>внимание {counts.warn}</span>
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" className={btn} disabled={test.isPending} onClick={() => test.mutate()} title="@unirenteraibot, @unirenterbot и бот MAX пишут владельцу «Проверка связи»">
              {test.isPending ? "Пишу…" : "Тест ботами"}
            </button>
            <button type="button" className={`${btn} border-accent text-fg`} disabled={scan.isPending} onClick={() => scan.mutate()}>
              {scan.isPending ? "Проверяю каналы…" : "Проверить сейчас"}
            </button>
          </div>
        </div>
        <p className="text-[12px] text-fg-faint">
          Каждый канал, к которому подключён prokat: проверки по своей базе и живые запросы в API площадок. Фон проверяет раз в 5 минут; сбой и
          восстановление {d?.send ? "приходят владельцу в личку от @unirenteraibot" : "в личку пока не пишем (выключено)"}.
        </p>
        {scan.data && (
          <p className="text-[12.5px] text-fg-muted" data-annot="итог проверки">
            Проверено строк {scan.data.scan.checks}, красных {scan.data.scan.fail}, новых сбоев {scan.data.scan.opened}, закрыто {scan.data.scan.resolved}
            {scan.data.scan.sent ? `, ушло в личку ${scan.data.scan.sent}` : ""}
            {scan.data.scan.errors.length > 0 && <span className="text-err"> · {scan.data.scan.errors.join("; ")}</span>}
          </p>
        )}
        {scan.error && <p className="text-[12.5px] text-err">{String(scan.error.message)}</p>}
        {tests && (
          <div className="flex flex-wrap gap-x-5 text-[12.5px]" data-annot="итог теста ботами">
            {tests.map((t) => (
              <span key={t.channel} className={TEXT[t.state]} title={t.detail}>
                {TEST_LABEL[t.channel] ?? t.channel}: {t.value}
              </span>
            ))}
          </div>
        )}
        {test.error && <p className="text-[12.5px] text-err">{String(test.error.message)}</p>}
      </div>
      {q.isPending && <p className="text-[12.5px] text-fg-muted">Загружаю…</p>}
      {q.error && <p className="text-[12.5px] text-err">{String(q.error)}</p>}
      {tab === "channels" && d && <Channels d={d} />}
      {tab === "alerts" && <Alerts d={d} />}
    </main>
  );
}
